import React from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowRight } from 'lucide-react';
import { blogPosts, blogCategories } from '../data/blogPosts';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StickyButtons from '../components/StickyButtons';
import { Toaster } from '../components/ui/sonner';

const BlogIndex = () => {
  const featured = blogPosts[0];
  const rest = blogPosts.slice(1);

  React.useEffect(() => {
    document.title = 'Blog & Research | Stocks Vision Academy — Bhopal Stock Market Training';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = 'Free practical stock market education — technical analysis, options trading, price action, risk management. By Deepak Singhai, NISM Certified RA, Stocks Vision Academy Bhopal.';

    // ItemList JSON-LD
    const ld = {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'Stocks Vision Academy Blog',
      description: 'Stock market education, options trading, technical analysis and risk management for Indian traders.',
      url: window.location.href,
      publisher: {
        '@type': 'EducationalOrganization',
        name: 'Stocks Vision Academy',
        address: 'MP Nagar, Bhopal, Madhya Pradesh, India',
      },
      blogPost: blogPosts.map((p) => ({
        '@type': 'BlogPosting',
        headline: p.title,
        description: p.metaDescription,
        url: `${window.location.origin}/blog/${p.slug}`,
        datePublished: p.publishedAt,
        author: { '@type': 'Person', name: 'Deepak Singhai' },
      })),
    };
    let script = document.getElementById('blog-list-jsonld');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'blog-list-jsonld';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);

    return () => {
      const s = document.getElementById('blog-list-jsonld');
      if (s) s.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <main className="pt-24 pb-20" data-testid="blog-index-page">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          {/* Header */}
          <div className="mb-12">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-green-700 font-semibold hover:underline mb-4">
              <Icons.ChevronLeft className="h-4 w-4" /> Back to Home
            </Link>
            <div className="inline-flex items-center gap-1.5 bg-green-100 text-green-800 px-3.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3">
              Free Market Education
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-3">
              Blog & <span className="text-green-600">Research</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl">
              Practical stock market education by <strong>Deepak Singhai</strong>, NISM Certified Research Analyst — for Indian traders who want clarity, not noise.
            </p>
          </div>

          {/* Featured Card */}
          <FeaturedCard post={featured} />

          {/* Rest of posts */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10" data-testid="blog-grid">
            {rest.map((post) => (
              <BlogCard key={post.slug} post={post} />
            ))}
          </div>
        </div>
      </main>
      <Footer />
      <StickyButtons />
      <Toaster />
    </div>
  );
};

const FeaturedCard = ({ post }) => {
  const Icon = Icons[post.icon] || Icons.BookOpen;
  const cat = blogCategories[post.category];
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group block bg-white border-2 border-gray-200 rounded-3xl overflow-hidden hover:border-green-600 transition-all duration-200"
      data-testid="blog-featured-card"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr]">
        <div className="bg-gradient-to-br from-green-100 to-green-50 flex items-center justify-center py-16 lg:py-20">
          <Icon className="h-24 w-24 text-green-600" strokeWidth={1.4} />
        </div>
        <div className="p-8 sm:p-10 flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-block bg-amber-100 text-amber-800 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded">Featured</span>
            <span className={`inline-block ${cat.color} text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded`}>{cat.label}</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-3 leading-tight group-hover:text-green-700 transition-colors">
            {post.title}
          </h2>
          <p className="text-gray-600 leading-relaxed mb-5">{post.excerpt}</p>
          <span className="inline-flex items-center gap-1 text-sm font-bold text-green-600 group-hover:gap-2 transition-all">
            Read Article <ArrowRight className="h-4 w-4" />
          </span>
        </div>
      </div>
    </Link>
  );
};

const BlogCard = ({ post }) => {
  const Icon = Icons[post.icon] || Icons.BookOpen;
  const cat = blogCategories[post.category];
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group block bg-white border-2 border-gray-200 rounded-2xl overflow-hidden hover:border-green-600 hover:-translate-y-1 transition-all duration-200"
      data-testid={`blog-card-${post.slug}`}
    >
      <div className="h-36 bg-green-100 flex items-center justify-center">
        <Icon className="h-14 w-14 text-green-600" strokeWidth={1.5} />
      </div>
      <div className="p-6">
        <span className={`inline-block ${cat.color} text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded mb-3`}>
          {cat.label}
        </span>
        <h3 className="text-base font-bold text-gray-900 leading-snug mb-2 group-hover:text-green-700 transition-colors">
          {post.title}
        </h3>
        <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3">{post.excerpt}</p>
        <span className="inline-flex items-center gap-1 text-sm font-bold text-green-600 group-hover:gap-2 transition-all">
          Read Article <ArrowRight className="h-4 w-4" />
        </span>
      </div>
    </Link>
  );
};

export default BlogIndex;
